import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/api-axios';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from './icons';
import styles from './Login.module.css';
import { fetchSsoConfig, signInWith } from '../../services/sso';

function LoginForm() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    function handleEmailChange(event) {
        setEmail(event.target.value);
    }

    function handlePasswordChange(event) {
        setPassword(event.target.value);
    }

    const handleSubmit = async () => {
        if (!email || !password) {
            setError({ message: 'Please fill in all fields' });
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await axios.post('/api/auth/login', {
                email: email,
                password: password
            });

            console.log(res.data["jwt-token"]);
            console.log("res: " + res.data);
            let token = res.data["jwt-token"];
            localStorage.setItem("SavedToken", 'Bearer ' + token);
            navigate("/home");
        } catch (error) {
            setError(error);
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const [ssoConfig, setSsoConfig] = useState(null);

    // Which providers appear is decided by the server, so an unconfigured provider
    // simply never renders a button.
    useEffect(() => {
        let cancelled = false;
        fetchSsoConfig().then(cfg => { if (!cancelled) setSsoConfig(cfg); });
        return () => { cancelled = true; };
    }, []);

    const handleSso = async (provider) => {
        setLoading(true);
        setError(null);
        try {
            await signInWith(provider, ssoConfig);
            navigate('/home');
        } catch (err) {
            setError({ message: err?.response?.data?.message || err?.message || 'Sign-in failed' });
        } finally {
            setLoading(false);
        }
    };

    const ssoButtons = [
        { id: 'google', label: 'Continue with Google', icon: 'G', enabled: ssoConfig?.google?.enabled },
        { id: 'facebook', label: 'Continue with Facebook', icon: 'f', enabled: ssoConfig?.facebook?.enabled },
        { id: 'apple', label: 'Continue with Apple', icon: '', enabled: ssoConfig?.apple?.enabled },
    ].filter(b => b.enabled);

    const handleGuestSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            // Guests need no email: the backend assigns a generated display name
            // and ignores the field. Only pass one through if the user typed it.
            const res = await axios.post('/api/auth/guest', email ? { email } : {});

            console.log("HANDLE GUEST SUBMIT2");
            console.log(res.data["jwt-token"]);
            console.log("res: " + res.data);
            let token = res.data["jwt-token"];
            localStorage.setItem("SavedToken", 'Bearer ' + token);
            navigate("/home");
        } catch (error) {
            console.log("HANDLE GUEST SUBMIT3");
            setError(error);
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className={styles.formContainer}>
            <div className={styles.header}>
                <h2 className={styles.title}>
                    Welcome Back
                </h2>
                <p className={styles.subtitle}>
                    Sign in to your account to continue
                </p>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Email Address
                    </label>
                    <div className={styles.inputWrapper}>
                        <MailIcon className={styles.inputIcon} />
                        <input
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="Enter your email..."
                            className={styles.input}
                        />
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Password
                    </label>
                    <div className={styles.inputWrapper}>
                        <LockIcon className={styles.inputIcon} />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={handlePasswordChange}
                            placeholder="Enter your password..."
                            className={styles.input}
                        />
                        <button
                            type="button"
                            className={styles.togglePassword}
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        <span>⚠️</span>
                        {error.message || 'An error occurred during login'}
                    </div>
                )}

                <div className={styles.buttonGroup}>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={styles.primaryButton}
                    >
                        {loading ? (
                            <>
                                <div className={styles.spinner} />
                                Signing In...
                            </>
                        ) : (
                            <>
                                🔐 Sign In
                            </>
                        )}
                    </button>

                    {ssoButtons.length > 0 && (
                        <div className={styles.ssoGroup}>
                            {ssoButtons.map(b => (
                                <button
                                    key={b.id}
                                    type="button"
                                    onClick={() => handleSso(b.id)}
                                    disabled={loading}
                                    className={`${styles.ssoButton} ${styles['sso_' + b.id]}`}
                                >
                                    <span className={styles.ssoIcon}>{b.icon}</span>
                                    {b.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={handleGuestSubmit}
                        disabled={loading}
                        className={styles.secondaryButton}
                    >
                        {loading ? (
                            <>
                                <div className={styles.spinner} />
                                Signing In...
                            </>
                        ) : (
                            <>
                                👤 Continue as <strong>GUEST</strong>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default LoginForm;